import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, Spinner } from '@klaro7/ui';

import {
  useFeatureFlags,
  useSetFeatureFlag,
  useDeleteFeatureFlag,
  useConfigStatus,
  useTestConfig,
} from '../hooks/useServicesSettings';
import type { ConfigTestResult } from '../services/settings.service';

type TestState = ConfigTestResult | 'pending';

const KEY_PATTERN = /^[a-zA-Z0-9_.-]{1,100}$/;

function parseValue(raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  try {
    return JSON.parse(trimmed);
  } catch {
    return raw; // keep as plain string
  }
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export function FeatureFlagsSection() {
  const { data: flags, isLoading } = useFeatureFlags();
  const { data: configStatus } = useConfigStatus(true);
  const setMutation = useSetFeatureFlag();
  const deleteMutation = useDeleteFeatureFlag();
  const testMutation = useTestConfig();

  // Per-key reachability test results (keyed by cfg.key)
  const [testResults, setTestResults] = useState<Record<string, TestState>>({});

  const clearTestResult = (key: string) => {
    setTestResults((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleTest = async (key: string) => {
    setTestResults((prev) => ({ ...prev, [key]: 'pending' }));
    try {
      const result = await testMutation.mutateAsync({ key });
      setTestResults((prev) => ({ ...prev, [key]: result }));
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [key]: {
          ok: false,
          ms: 0,
          message: e instanceof Error ? e.message : 'Errore durante il test',
        },
      }));
    }
  };

  const entries = useMemo(
    () => Object.entries(flags ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    [flags],
  );

  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const sectionRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  // Deep-link from the config banner: scroll into view + flash highlight
  const [highlight, setHighlight] = useState(false);
  useEffect(() => {
    if (searchParams.get('section') !== 'feature-flags') return;
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlight(true);
    const t = setTimeout(() => setHighlight(false), 2500);
    return () => clearTimeout(t);
  }, [searchParams]);

  const quickFill = async (key: string, suggestion: string) => {
    setError('');
    clearTestResult(key);
    try {
      await setMutation.mutateAsync({ key, value: parseValue(suggestion) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    }
  };

  const handleAdd = async () => {
    setError('');
    const key = newKey.trim();
    if (!KEY_PATTERN.test(key)) {
      setError('Chiave non valida (max 100 caratteri, [a-zA-Z0-9_.-])');
      return;
    }
    try {
      await setMutation.mutateAsync({ key, value: parseValue(newValue) });
      setNewKey('');
      setNewValue('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    }
  };

  const handleUpdate = async (key: string) => {
    setError('');
    clearTestResult(key);
    const raw = editValues[key] ?? formatValue(flags?.[key]);
    try {
      await setMutation.mutateAsync({ key, value: parseValue(raw) });
      setEditValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (key: string) => {
    setError('');
    clearTestResult(key);
    try {
      await deleteMutation.mutateAsync(key);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante la rimozione');
    }
  };

  return (
    <div
      ref={sectionRef}
      className={`rounded-xl border bg-white p-6 transition-shadow ${
        highlight ? 'border-red-400 shadow-[0_0_0_3px_rgba(248,113,113,0.4)]' : 'border-neutral-200'
      }`}
    >
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-neutral-900">Feature Flags</h2>
        <p className="mt-1 text-sm text-neutral-600">
          Configurazioni runtime salvate sul database. I valori sono interpretati come JSON quando
          possibile (es. <code>true</code>, <code>42</code>, <code>{'{"a":1}'}</code>), altrimenti
          stringa.
        </p>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Required config keys (manifest) */}
      {configStatus && configStatus.length > 0 && (
        <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
          <h3 className="mb-3 text-sm font-semibold text-neutral-800">Configurazione richiesta</h3>
          <ul className="space-y-3">
            {configStatus.map((cfg) => (
              <li key={cfg.key} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block h-2 w-2 shrink-0 rounded-full ${
                        cfg.configured ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    />
                    <span className="text-sm font-medium text-neutral-900">{cfg.label}</span>
                    <code className="text-xs text-neutral-400">{cfg.key}</code>
                  </div>
                  <p className="ml-4 text-xs text-neutral-500">{cfg.description}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <TestResultBadge result={testResults[cfg.key]} />
                  {!cfg.configured ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => quickFill(cfg.key, cfg.suggestion)}
                      loading={setMutation.isPending}
                    >
                      Usa «{cfg.suggestion}»
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTest(cfg.key)}
                      loading={testResults[cfg.key] === 'pending'}
                      disabled={testResults[cfg.key] === 'pending'}
                    >
                      Testa
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Add new flag */}
      <div className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_2fr_auto]">
        <Input
          placeholder="chiave (es. enable_video_call)"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
        />
        <Input
          placeholder='valore (es. true, 42, "ciao", {"a":1})'
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
        />
        <Button onClick={handleAdd} loading={setMutation.isPending}>
          Aggiungi
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : entries.length === 0 ? (
        <p className="py-6 text-center text-sm text-neutral-500">
          Nessun feature flag configurato.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-200">
          {entries.map(([key, value]) => {
            const editing = key in editValues;
            const display = editing ? editValues[key] : formatValue(value);
            return (
              <li key={key} className="grid grid-cols-1 items-center gap-2 py-3 sm:grid-cols-[1fr_2fr_auto]">
                <code className="break-all text-sm font-medium text-neutral-900">{key}</code>
                <Input
                  value={display}
                  onChange={(e) =>
                    setEditValues((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleUpdate(key)}
                    loading={setMutation.isPending}
                    disabled={!editing}
                  >
                    Salva
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(key)}
                    loading={deleteMutation.isPending}
                  >
                    Rimuovi
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TestResultBadge({ result }: { result: TestState | undefined }) {
  if (!result) return null;

  if (result === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
        <Spinner size="sm" />
        Test in corso…
      </span>
    );
  }

  const { ok, status, ms, message } = result;
  const isServerError = ok && ((status !== undefined && status >= 500) || message === 'Raggiungibile ma errore server');

  if (ok && !isServerError) {
    return (
      <span className="inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
        ✓ OK{status !== undefined ? ` ${status}` : ''} · {ms}ms
      </span>
    );
  }

  if (isServerError) {
    return (
      <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        ⚠ {status !== undefined ? status : '5xx'} · {ms}ms
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
      ✗ {message}
    </span>
  );
}
